/**
 * FEAT-087 / S-394 — which agent's stops you always wave through.
 *
 * The design calls this "the only judgement on this screen the user can act
 * on", and its own fixture is the specification:
 *
 *   Publisher     approval gate      14 stops, 12 waved through
 *   Auditor       unverified claims   9 stops,  3 waved through
 *   Art Director  image choice        7 stops,  7 waved through   ← marked
 *   Editor        voice exceptions    4 stops,  1 waved through
 *   Librarian     outline review      2 stops,  2 waved through
 *
 * Two things are load-bearing there, and both are easy to get wrong:
 *
 * **The Publisher is not marked, despite 12 of 14.** So the rule is not a high
 * ratio. It is "always" — the design's own word — and 12 of 14 is not always.
 * An agent that occasionally catches something is earning its interruption.
 *
 * **The Librarian is not marked, despite 2 of 2.** Same perfect ratio as the Art
 * Director. So a minimum sample gates it, and the design is showing that two
 * stops is not evidence of anything.
 *
 * Shared rather than living in the view because the threshold is a claim about
 * the data, not a detail of how it is drawn.
 */

/** One agent's stops over the reported period. */
export interface AgentStopRow {
  /** Display name — "Art Director". */
  agent: string;
  /** What it stops for, in the agent's own words where S-380 recorded one. */
  reason: string | null;
  /** Times this agent stopped a piece and a person then decided. */
  stops: number;
  /** Of those, the times the person continued without asking for a change. */
  waved_through: number;
}

/**
 * The fewest stops that can support the accusation.
 *
 * The fixture excludes 2 and includes 7, so it is somewhere between. 5 is the
 * smallest value the claim survives: if an agent's stops were genuinely worth
 * heeding half the time, five straight wave-throughs has probability 1/32, near
 * enough 3%. At 3 it is 12.5% — often enough by chance that the screen would
 * eventually accuse an agent that did nothing wrong, which is worse than saying
 * nothing at all.
 */
export const ALWAYS_WAVED_MIN_STOPS = 5;

/** Share of this agent's stops that changed nothing. `null` when it never stopped. */
export function wavedThroughRate(row: AgentStopRow): number | null {
  if (row.stops <= 0) return null;
  return row.waved_through / row.stops;
}

/** Did every one of this agent's stops end in the person just carrying on? */
export function isAlwaysWavedThrough(row: AgentStopRow): boolean {
  return row.stops >= ALWAYS_WAVED_MIN_STOPS && row.waved_through === row.stops;
}

/**
 * The single agent to mark, or `null` when none has earned it.
 *
 * One row, never several: the handoff rations loud marks to one amber callout
 * per screen, and a screen that flags three things flags nothing. When more than
 * one agent qualifies the most-stopped wins, because that is the one costing the
 * most time. Ties break on name so the mark does not move between renders of the
 * same data.
 */
export function markedAgent(rows: AgentStopRow[]): string | null {
  const candidates = rows.filter(isAlwaysWavedThrough);
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.stops - a.stops || a.agent.localeCompare(b.agent));
  return candidates[0].agent;
}

/**
 * Which agent a recorded stop belongs to.
 *
 * Mirrors the Desk queue's `stoppedByAgent`, deliberately — the same stop must
 * not be called "Art Director" on one screen and "Publisher" on another.
 *
 * The approval gate is why this is not a plain phase lookup. A piece held at the
 * gate still reads `generation_phases = phase_7_art_directed`, because the Art
 * Director genuinely finished; the Publisher is the one refusing to go on. The
 * fixture separates them as different rows ("Publisher · approval gate" and
 * "Art Director · image choice"), so the recorder stores `awaiting_approval` for
 * a gate and the phase for everything else.
 */
export function agentForStop(
  phase: string | null | undefined,
  phaseLabels: readonly { value: string; label: string }[]
): string {
  if (!phase) return "Pipeline";
  if (phase === "awaiting_approval") return "Publisher";
  return phaseLabels.find((p) => p.value === phase)?.label ?? "Pipeline";
}
